import React, { useCallback, useState } from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { BackHandler, Platform, Pressable, Text, View } from 'react-native';
import AppSearchBar from '../../components/Reusables/AppSearchBar';
import { useFocusEffect } from '@react-navigation/native';
import { Menu } from 'react-native-paper';
import { isTablet } from 'react-native-device-info';
import Zocial from '@react-native-vector-icons/zocial/static';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import Typography from '../../components/Reusables/Typography';
import { GTFonts, useLayoutSize } from '../../theme';
import DemoIntroScreen from '../../screens/auth/DemoIntroScreen';
import DemoProductsScreen from '../../screens/demo/DemoProductsScreen';
import DemoCartScreen from '../../screens/demo/DemoCartScreen';
import DemoCheckoutScreen from '../../screens/demo/DemoCheckoutScreen';
import type { DemoCheckoutNavigationPayload } from '../../models/demo/orders';
import moment from 'moment';
import { fs, iconSize, s } from '../../utils/libs';
import AppSnackbar from '../../services/AppSnackbar';
import WebSidebarLayout from '../../components/web/WebSidebarLayout';
import WebHeaderBarInjector from '../../components/web/WebHeaderBarInjector';
import WebHeaderBar, {
  type WebHeaderBarProps,
} from '../../components/web/WebHeaderBar';
import { useDemoCart } from '../../hooks/demo';
import HeaderLeft from '../../components/Reusables/headers/HeaderLeft';
import HeaderIconButton from '../../components/Reusables/headers/HeaderIconButton';
import { confirmQuitDemoAndGoToOnboarding } from './confirmQuitDemo';

const showDemoFeatureNotSupported = (featureLabel: string) => {
  AppSnackbar.show(
    `${featureLabel} is not supported in demo mode, Please create a BizPlus POS account to use this feature.`,
    'info',
    { durationMs: 4500 },
  );
};

const HeaderOnlyWebFrame: React.FC<
  WebHeaderBarProps & { children: React.ReactNode }
> = ({ children, ...headerProps }) => {
  const { useSidebarNav } = useLayoutSize();

  if (!useSidebarNav) {
    return <>{children}</>;
  }

  return (
    <View style={{ flex: 1 }}>
      <WebHeaderBar {...headerProps} />
      <View style={{ flex: 1, minWidth: 0 } as any}>{children}</View>
    </View>
  );
};

const DemoProductsScreenWithLayout = (props: any) => {
  const { useSidebarNav } = useLayoutSize();
  const [cartCount, setCartCount] = useState(0);
  if (useSidebarNav) {
    return (
      <WebSidebarLayout isDemoMode cartBadgeOverride={cartCount}>
        <DemoProductsScreen {...props} onCartCountReport={setCartCount} />
      </WebSidebarLayout>
    );
  }
  return <DemoProductsScreen {...props} />;
};

const DemoCartMenuButton = () => {
  const [menuVisible, setMenuVisible] = useState(false);
  return (
    <Menu
      visible={menuVisible}
      onDismiss={() => setMenuVisible(false)}
      anchor={
        <HeaderIconButton
          tooltip="Open basket menu"
          onPress={() => setMenuVisible(true)}
          style={{ padding: s(6) }}
          accessibilityLabel="Open basket menu">
          <MaterialCommunityIcons
            name="dots-vertical"
            size={iconSize(isTablet() ? 24 : 26)}
            color="#fff"
          />
        </HeaderIconButton>
      }
      anchorPosition="bottom">
      <Menu.Item
        title="Basket Settings"
        leadingIcon="cog-outline"
        onPress={() => {
          setMenuVisible(false);
          showDemoFeatureNotSupported('Basket Settings');
        }}
      />
    </Menu>
  );
};

const DemoCheckoutHeaderTitle: React.FC<{ receiptID?: string }> = ({
  receiptID,
}) =>
  receiptID ? (
    <Text
      style={{
        color: 'white',
        fontFamily: GTFonts.semibold,
        fontSize: fs(Platform.OS === 'web' ? 14 : 16),
        lineHeight: fs(isTablet() ? 20 : 18, {
          scaleMode: 'height',
        }),
      }}>
      Receipt ID: {receiptID}
    </Text>
  ) : (
    <Text
      style={{
        color: 'white',
        fontFamily: GTFonts.regular,
        fontSize: fs(Platform.OS === 'web' ? 14 : 16),
        lineHeight: fs(isTablet() ? 20 : 18, {
          scaleMode: 'height',
        }),
      }}>
      Checkout
    </Text>
  );

const DemoCheckoutHeaderRight = () => {
  const [menuVisible, setMenuVisible] = useState(false);

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
      }}>
      <Pressable
        onPress={() => {
          AppSnackbar.show(
            'Receipt printing is not supported in demo mode. Create a BizPlus POS account to use this feature.',
            'info',
            { durationMs: 4500 },
          );
        }}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: s(5),
          marginRight: s(4),
        }}
        accessibilityRole="button"
        accessibilityLabel="Print demo receipt">
        <Text
          style={{
            fontFamily: GTFonts.semibold,
            color: '#fff',
            fontSize: fs(Platform.OS === 'web' ? 14 : 16),
            lineHeight: fs(isTablet() ? 20 : 18),
          }}>
          Print
        </Text>
        <Zocial name="print" size={iconSize(22)} color="#fff" />
      </Pressable>

      <Menu
        visible={menuVisible}
        onDismiss={() => setMenuVisible(false)}
        anchor={
          <HeaderIconButton
            tooltip="Open checkout menu"
            onPress={() => setMenuVisible(true)}
            style={{ padding: s(6) }}
            accessibilityLabel="Open checkout menu">
            <MaterialCommunityIcons
              name="dots-vertical"
              size={iconSize(isTablet() ? 24 : 26)}
              color="#fff"
            />
          </HeaderIconButton>
        }
        anchorPosition="bottom">
        <Menu.Item
          leadingIcon="cog"
          onPress={() => {
            setMenuVisible(false);
          }}
          title="Checkout Settings"
        />
      </Menu>
    </View>
  );
};

const DemoCartScreenWithLayout = (props: any) => {
  const { useSidebarNav } = useLayoutSize();
  const { cartCount } = useDemoCart();
  if (useSidebarNav) {
    return (
      <WebSidebarLayout isDemoMode cartBadgeOverride={cartCount}>
        <WebHeaderBarInjector
          titleAlign="center"
          showBack={false}
          title={
            <View style={{ alignItems: 'center', justifyContent: 'center' }}>
              <Typography
                style={{
                  color: 'white',
                  fontFamily: GTFonts.regular,
                  fontSize: fs(16),
                  lineHeight: fs(20),
                }}>
                Basket
              </Typography>
              <Typography
                style={{
                  color: 'white',
                  fontFamily: GTFonts.regular,
                  fontSize: fs(12),
                  lineHeight: fs(16),
                  opacity: 0.95,
                  marginTop: 1,
                }}>
                {moment(new Date()).format('MMM Do YYYY, h:mm:ss a')}
              </Typography>
            </View>
          }
          rightSlot={<DemoCartMenuButton />}
        />
        <DemoCartScreen {...props} />
      </WebSidebarLayout>
    );
  }
  return <DemoCartScreen {...props} />;
};

const DemoCheckoutScreenWithLayout = (props: any) => {
  const receiptID = props.route?.params?.ReceiptID;

  return (
    <HeaderOnlyWebFrame
      title={<DemoCheckoutHeaderTitle receiptID={receiptID} />}
      showBack={false}
      rightSlot={<DemoCheckoutHeaderRight />}>
      <DemoCheckoutScreen {...props} />
    </HeaderOnlyWebFrame>
  );
};

// Future demo screens will be imported here:
// import DemoProductsScreen from '../../screens/demo/DemoProductsScreen';
// import DemoCartScreen from '../../screens/demo/DemoCartScreen';
// import DemoPaymentScreen from '../../screens/demo/DemoPaymentScreen';
// import DemoReceiptScreen from '../../screens/demo/DemoReceiptScreen';

export type DemoStackParamList = {
  DemoIntro: undefined;
  DemoProducts: undefined;
  DemoCart: undefined;
  DemoCheckout: DemoCheckoutNavigationPayload;
  // Future screens:
  // DemoCart: undefined;
  // DemoPayment: undefined;
  // DemoReceipt: undefined;
};

const Stack = createNativeStackNavigator<DemoStackParamList>();

const DemoStack = () => {
  const { width, useSidebarNav } = useLayoutSize();
  const isTabletCompact = width >= 768 && width < 1024;
  const webHeaderSearchMaxWidth = isTabletCompact ? s(660) : s(620);
  const nativeHeaderSearchWidth = isTablet()
    ? Math.max(width * 0.7, width - s(110))
    : width * 0.7;

  const handleDemoBarcodeScan = useCallback(() => {
    AppSnackbar.show(
      'Scan-to-sell is not available in demo mode. Create a BizPlus POS account to use this feature.',
      'info',
    );
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS !== 'android') {
        return undefined;
      }

      const backSubscription = BackHandler.addEventListener(
        'hardwareBackPress',
        () => true,
      );

      return () => {
        backSubscription.remove();
      };
    }, []),
  );

  return (
    <Stack.Navigator
      initialRouteName="DemoIntro"
      screenOptions={{
        headerShown: false,
        gestureEnabled: false,
        headerBackVisible: false,
        animation: 'slide_from_right',
      }}>
      <Stack.Screen
        name="DemoIntro"
        component={DemoIntroScreen}
        options={{
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="DemoProducts"
        component={DemoProductsScreenWithLayout}
        options={({ navigation }) => ({
          animation: useSidebarNav ? 'none' : 'slide_from_right',
          headerShown: !useSidebarNav,
          headerBackVisible: false,
          headerStyle: { backgroundColor: '#1560bd' },
          headerTintColor: '#fff',
          headerTitle: () => (
            <View
              style={{
                width: '100%',
                maxWidth:
                  Platform.OS === 'web'
                    ? webHeaderSearchMaxWidth
                    : nativeHeaderSearchWidth,
                paddingHorizontal: s(4),
              }}>
              <AppSearchBar
                placeholder="Enter product name"
                contextKey="search"
                width={
                  Platform.OS === 'web'
                    ? undefined
                    : nativeHeaderSearchWidth
                }
                showLeadingIcon={true}
                showBarcodeScan={true}
                onBarcodeScan={handleDemoBarcodeScan}
                containerStyle={{
                  backgroundColor: '#fff',
                  borderColor: '#fff',
                  borderWidth: 0,
                }}
              />
            </View>
          ),
          headerLeft: () => (
            <HeaderLeft
              onPress={() => confirmQuitDemoAndGoToOnboarding(navigation)}
            />
          ),
          headerRight: () => {
            const [menuVisible, setMenuVisible] = useState(false);
            const handleDemoMenuItemPress = (featureLabel: string) => {
              setMenuVisible(false);
              showDemoFeatureNotSupported(featureLabel);
            };
            return (
              <Menu
                visible={menuVisible}
                onDismiss={() => setMenuVisible(false)}
                anchor={
                  <HeaderIconButton
                    tooltip="Open product menu"
                    onPress={() => setMenuVisible(true)}
                    style={{ padding: s(6) }}
                    accessibilityLabel="Open product menu">
                    <MaterialCommunityIcons
                      name="dots-vertical"
                      size={iconSize(isTablet() ? 24 : 26)}
                      color="#fff"
                    />
                  </HeaderIconButton>
                }
                anchorPosition="bottom">
                <Menu.Item
                  title="Add Product"
                  leadingIcon="plus"
                  onPress={() => handleDemoMenuItemPress('Add Product')}
                />
                <Menu.Item
                  title="Product Settings"
                  leadingIcon="cog-outline"
                  onPress={() => handleDemoMenuItemPress('Product Settings')}
                />
                <Menu.Item
                  title="Sort Products"
                  leadingIcon="sort-variant"
                  onPress={() => handleDemoMenuItemPress('Sort Products')}
                />
              </Menu>
            );
          },
        })}
      />
      <Stack.Screen
        name="DemoCart"
        component={DemoCartScreenWithLayout}
        options={{
          animation: useSidebarNav ? 'none' : 'slide_from_right',
          headerShown: !useSidebarNav,
          headerBackVisible: false,
          gestureEnabled: false,
          headerStyle: { backgroundColor: '#1560bd' },
          headerTintColor: '#fff',
          headerTitleAlign: 'center',
          headerTitle: () => (
            <View style={{ alignItems: 'center', justifyContent: 'center' }}>
              <Typography
                style={{
                  color: 'white',
                  fontFamily: GTFonts.regular,
                  fontSize: fs(16),
                  lineHeight: fs(20),
                }}>
                Basket
              </Typography>
              <Typography
                style={{
                  color: 'white',
                  fontFamily: GTFonts.regular,
                  fontSize: fs(12),
                  lineHeight: fs(16),
                  opacity: 0.95,
                  marginTop: 1,
                }}>
                {moment(new Date()).format('MMM Do YYYY, h:mm:ss a')}
              </Typography>
            </View>
          ),
          headerLeft: () => <View style={{ width: 34 }} />,
          headerRight: () => {
            const [menuVisible, setMenuVisible] = useState(false);
            return (
              <Menu
                visible={menuVisible}
                onDismiss={() => setMenuVisible(false)}
                anchor={
                  <HeaderIconButton
                    tooltip="Open basket menu"
                    onPress={() => setMenuVisible(true)}
                    style={{ padding: s(6) }}
                    accessibilityLabel="Open basket menu">
                    <MaterialCommunityIcons
                      name="dots-vertical"
                      size={iconSize(isTablet() ? 24 : 26)}
                      color="#fff"
                    />
                  </HeaderIconButton>
                }
                anchorPosition="bottom">
                <Menu.Item
                  title="Basket Settings"
                  leadingIcon="cog-outline"
                  onPress={() => {
                    setMenuVisible(false);
                    showDemoFeatureNotSupported('Basket Settings');
                  }}
                />
              </Menu>
            );
          },
        }}
      />
      <Stack.Screen
        name="DemoCheckout"
        component={DemoCheckoutScreenWithLayout}
        options={({ route }: any) => {
          const receiptID = route?.params?.ReceiptID;
          return {
            animation: useSidebarNav ? 'none' : 'slide_from_right',
            headerShown: !useSidebarNav,
            headerBackVisible: false,
            gestureEnabled: false,
            headerTitleAlign: 'left',
            headerTitle: () =>
              receiptID ? (
                <Text
                  style={{
                    color: 'white',
                    fontFamily: GTFonts.semibold,
                    fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                    lineHeight: fs(isTablet() ? 20 : 18, {
                      scaleMode: 'height',
                    }),
                  }}>
                  Receipt ID: {receiptID}
                </Text>
              ) : (
                <Text
                  style={{
                    color: 'white',
                    fontFamily: GTFonts.regular,
                    fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                    lineHeight: fs(isTablet() ? 20 : 18, {
                      scaleMode: 'height',
                    }),
                  }}>
                  Checkout
                </Text>
              ),
            headerStyle: { backgroundColor: '#1560bd' },
            headerTintColor: '#fff',
            headerRight: () => <DemoCheckoutHeaderRight />,
          };
        }}
      />
      {/* Future demo screens will be added here:
      <Stack.Screen
        name="DemoProducts"
        component={DemoProductsScreen}
        options={{
          headerShown: true,
          title: 'Select Products',
          headerStyle: { backgroundColor: theme.colors.primary },
          headerTintColor: '#fff',
        }}
      />
      <Stack.Screen
        name="DemoCart"
        component={DemoCartScreen}
        options={{
          headerShown: true,
          title: 'Review Order',
          headerStyle: { backgroundColor: theme.colors.primary },
          headerTintColor: '#fff',
        }}
      />
      <Stack.Screen
        name="DemoPayment"
        component={DemoPaymentScreen}
        options={{
          headerShown: true,
          title: 'Payment',
          headerStyle: { backgroundColor: theme.colors.primary },
          headerTintColor: '#fff',
        }}
      />
      <Stack.Screen
        name="DemoReceipt"
        component={DemoReceiptScreen}
        options={{
          headerShown: true,
          title: 'Receipt',
          headerStyle: { backgroundColor: theme.colors.primary },
          headerTintColor: '#fff',
        }}
      />
      */}
    </Stack.Navigator>
  );
};

export default DemoStack;
